import { Stack } from '@mui/material'
import { useAtomValue } from 'jotai'
import { memo } from 'react'
import { StringFormatter } from 'utils/StringFormatter'
import { getOrderSide } from 'utils/order'
import { SpotOrderRecord, SpotOrderStatus } from '../../api/v1/spot'
import {
  useOrderFilledPercentage,
  useOrderFilledSize,
  useOrderValue,
} from '../../hooks/useSizeCalculations'
import { useSpotSymbols } from '../../store/spot/hooks'
import { OrderDirection } from '../../store/spot/types'
import { getSpotFeeCurrency } from '../../utils/fee'
import { getEffectiveDecimals, toFixed } from '../../utils/number'
import { findSymbolById } from '../../utils/symbols'
import { OrderStatus } from '../OrderStatus'
import {
  orderModalAtom,
  OrderModalData,
  useCloseOrderModal,
  useUpdateModalTitle,
} from './index'
import {
  CancelButton,
  OrderModalRow,
  OrderModalRowInfo,
  OrderModalRowLabel,
} from './styles'
import { DateFormatter } from '../../utils/datetime'

export const SpotOrderHistoryModal = memo<{
  openOrderCancel?: (data: OrderModalData) => void
}>(({ openOrderCancel }) => {
  const orderData = useAtomValue(orderModalAtom)
  const spotSymbols = useSpotSymbols()
  const closeModal = useCloseOrderModal()

  const data = orderData?.data as SpotOrderRecord
  useUpdateModalTitle(data?.symbol)

  const {
    id = 0,
    symbolId = 0,
    type = '',
    direction = OrderDirection.LONG,
    chargeQuote = false,
    quantity = 0,
    unfilledQuantity = 0,
    price = 0,
    fillPrice = 0,
    makerFeeRate = 0,
    fee = 0,
    status = SpotOrderStatus.PENDING,
    createdAt = 0,
  } = data

  const symbol = findSymbolById(spotSymbols, Number(symbolId))
  const {
    baseScale = 0,
    quoteScale = 0,
    baseCurrency = '',
    quoteCurrency = '',
  } = symbol

  const fixedSize = toFixed(quantity, baseScale)
  const fixedPrice = toFixed(price, quoteScale)

  const filledSize = useOrderFilledSize(quantity, unfilledQuantity)
  const value = useOrderValue(quantity, price)
  const filledValue = useOrderValue(filledSize, fillPrice)
  const fixedFillPrice = toFixed(fillPrice, quoteScale)
  const fixedFilledSize = toFixed(filledSize, baseScale)
  const fixedValue = toFixed(value, quoteScale)
  const fixedFilledValue = toFixed(filledValue, quoteScale)

  const filledPercent = useOrderFilledPercentage(quantity, filledSize)

  const datetime = DateFormatter.toISODateTime(createdAt)

  return (
    <Stack>
      <OrderModalRow>
        <OrderModalRowLabel>
          <div>Side</div>
          <div>Type</div>
        </OrderModalRowLabel>
        <OrderModalRowInfo>
          <div className={getOrderSide(direction, 'lowerCase')}>
            {getOrderSide(direction)}
          </div>
          <div>{StringFormatter.startCase(type)}</div>
        </OrderModalRowInfo>
      </OrderModalRow>
      <OrderModalRow>
        <OrderModalRowLabel>
          <div>Size</div>
          <div>Filled Size</div>
        </OrderModalRowLabel>
        <OrderModalRowInfo>
          <div className={getOrderSide(direction, 'lowerCase')}>
            {fixedSize} {baseCurrency}
          </div>
          <div>
            {fixedFilledSize} {baseCurrency} ({filledPercent})
          </div>
        </OrderModalRowInfo>
      </OrderModalRow>
      <OrderModalRow>
        <OrderModalRowLabel>
          <div>Price</div>
          <div>Avg Fill Price</div>
        </OrderModalRowLabel>
        <OrderModalRowInfo>
          <div>
            {fixedPrice} {quoteCurrency}
          </div>
          <div>{fillPrice > 0 ? fixedFillPrice + quoteCurrency : '-'}</div>
        </OrderModalRowInfo>
      </OrderModalRow>
      <OrderModalRow>
        <OrderModalRowLabel>
          <div>Value</div>
          <div>Filled Value</div>
        </OrderModalRowLabel>
        <OrderModalRowInfo>
          <div>
            {fixedValue} {quoteCurrency}
          </div>
          <div>
            {fixedFilledValue} {quoteCurrency}
          </div>
        </OrderModalRowInfo>
      </OrderModalRow>
      <OrderModalRow>
        <OrderModalRowLabel>
          <div>Order ID</div>
          <div>Fee</div>
        </OrderModalRowLabel>
        <OrderModalRowInfo>
          <div>{id}</div>
          <div>
            {getEffectiveDecimals(fee)}{' '}
            {getSpotFeeCurrency(
              direction,
              chargeQuote,
              makerFeeRate,
              baseCurrency,
              quoteCurrency,
            )}
          </div>
        </OrderModalRowInfo>
      </OrderModalRow>
      <OrderModalRow>
        <OrderModalRowLabel>
          <div>Time</div>
          <div>Status</div>
        </OrderModalRowLabel>
        <OrderModalRowInfo>
          <div>{datetime}</div>
          <div className={status}>
            <OrderStatus status={status} />
          </div>
        </OrderModalRowInfo>
      </OrderModalRow>
      {openOrderCancel ? (
        <CancelButton
          onClick={() => {
            openOrderCancel && openOrderCancel(data)
            closeModal && closeModal()
          }}
        >
          Cancel Order
        </CancelButton>
      ) : null}
    </Stack>
  )
})
